import type { Attachment } from '../../../../store/previewer/types'
import type { TimelineEvent, TimelineGroup, TimelineProps } from '../types'

import { CloseOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Input, Select, Spin, Tooltip } from 'antd'
import cx from 'classnames'
import { format, formatISO, parseISO } from 'date-fns'
import { Fragment, useMemo, useState } from 'react'
import { GroupedVirtuoso } from 'react-virtuoso'

import $api, { API_URL } from '../../../../API'
import * as previewActions from '../../../../store/previewer'
import {
  AVAILABLE_FILE_TYPES_MAPPER,
  eventFileTypeConfigMapper,
  pickupSourceNameMapper,
  reportFileTypeMapper,
  reportsTypes,
  timelineEventTypes
} from '../constants'
import { currencyFormatter, formatGroupMeta, getFileHref, getFileName } from '../functions'

import { useAppDispatch } from '../../../../store'
import { DropoffTimeline } from './dropoff-timeline'

export function Timeline({ id, onClose }: TimelineProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])

  const dispatch = useAppDispatch()

  const query = useQuery({
    queryKey: ['check-runs', id, 'events'],

    async queryFn({ queryKey }) {
      const [, id] = queryKey
      const response = await $api.get<TimelineEvent[]>(`/check-runs/${id}/events`)

      return response.data
    },

    select(data) {
      return data.map(event => ({
        ...event,
        date: new Date(event.date)
      })) as TimelineEvent[]
    },

    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

  const filteredEvents = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase()

    return (query.data ?? [])
      .filter(event => {
        if (normalizedSearchQuery.length === 0) {
          return true
        }

        const search = [
          event.type,
          'name' in event.data && event.data.name,
          'filename' in event.data && event.data.filename,
          'type' in event.data && event.data.type,
          'original_filename' in event.data && event.data.original_filename,
          'source' in event.data && event.data.source,
          'status' in event.data && event.data.status,
          'exceptionType' in event.data && event.data.exceptionType
        ]
          .filter(Boolean)
          .map(value => value.toString().toLowerCase())
          .join(' ')

        const words = normalizedSearchQuery.split(/\s+/)
        return words.some(word => search.includes(word))
      })
      .filter(event => (selectedTypes.length > 0 ? selectedTypes.includes(event.type) : true))
  }, [query.data, searchQuery, selectedTypes])

  const groups = useMemo<TimelineGroup[]>(() => {
    return filteredEvents
      .reduce<TimelineGroup[]>((acc, event) => {
        const key = formatISO(event.date, { representation: 'date' })
        let existing = acc.find(group => group.key === key)

        if (!existing) {
          existing = {
            key,
            events: []
          }

          acc = [...acc, existing]
        }

        existing.events = [...existing.events, event]

        return acc
      }, [])
      .sort((a, b) => b.key.localeCompare(a.key))
  }, [filteredEvents])

  const events = useMemo(() => {
    return groups.flatMap(group => group.events)
  }, [groups])

  const counts = useMemo(() => {
    return groups.map(group => group.events.length)
  }, [groups])

  const [year, setYear] = useState(events[0]?.date.getFullYear())
  const token = window.localStorage.getItem('token')

  const handlePdfPreviewClick = (file: Attachment) => (): void => {
    dispatch(previewActions.open(file))
  }

  if (query.isLoading) {
    return (
      <div className='flex flex-col justify-center items-center p-8'>
        <Spin />
      </div>
    )
  }

  return (
    <div className='flex flex-col'>
      <div className='flex flex-row gap-2 justify-start items-center px-7 pr-8 pb-2'>
        <span className='text-xl font-semibold font-lato text-lucent-text'>{year}</span>

        <Input
          type='search'
          className='ml-4'
          placeholder='Search for events...'
          prefix={<SearchOutlined className='text-gray-400' />}
          value={searchQuery}
          onChange={event => {
            setSearchQuery(event.target.value)
          }}
        />

        <Select
          placeholder='Filter by type'
          mode='multiple'
          allowClear
          maxTagCount='responsive'
          className='w-80 text-left'
          options={timelineEventTypes.map(type => {
            const config = eventFileTypeConfigMapper[type]

            return {
              label:
                typeof config?.title === 'string'
                  ? config.title
                  : config?.title && typeof config.title === 'function'
                    ? (config.title({ type, data: {} } as Parameters<typeof config.title>[0]) ??
                      type)
                    : type,

              value: type
            }
          })}
          value={selectedTypes}
          onChange={value => {
            setSelectedTypes(value)
          }}
        />

        <button type='button' className='p-2 -m-2 ml-8' title='Close Timeline' onClick={onClose}>
          <CloseOutlined className='text-sm text-gray-400' />
        </button>
      </div>
      <div className='flex flex-row'>
        <GroupedVirtuoso
          className='w-full !h-[20rem] relative'
          // data={data}
          // context={context}
          groupCounts={counts}
          groupContent={index => (
            <div className='pl-[26px] px-6 py-1 text-left ?bg-lucent-background'>
              <span className='font-lato text-xs font-semibold text-lucent-text bg-gray-100 px-2 py-1.5 inline-block rounded'>
                {format(parseISO(groups[index]?.key || ''), 'MM/dd')}
              </span>
            </div>
          )}
          itemContent={index => {
            const event = events[index]
            const config = event?.type ? eventFileTypeConfigMapper[event.type] : undefined

            return (
              <div className='grid grid-cols-[repeat(4,_max-content)_1fr] gap-4 items-center justify-start py-2'>
                <div className='font-lato text-xs text-lucent-text pl-[34px] pr-2'>
                  {event ? format(event.date, 'HH:mm') : '??:??'}
                </div>

                {config?.icon && (
                  <>
                    {event?.type && AVAILABLE_FILE_TYPES_MAPPER[event.type] && (
                      <button
                        className='flex justify-center items-center -translate-y-[3px]'
                        onClick={handlePdfPreviewClick({
                          mimeType: AVAILABLE_FILE_TYPES_MAPPER[event.type],
                          name: getFileName(event),
                          href: getFileHref(event, token)
                        })}
                      >
                        <SearchOutlined />
                      </button>
                    )}

                    <config.icon className={cx('text-base -translate-y-[3px]', config?.color)} />
                  </>
                )}

                {!config?.icon && <div />}

                <div className='font-lato text-xs font-semibold text-lucent-text px-2 py-1.5 bg-gray-100 rounded'>
                  {typeof config?.title === 'string'
                    ? config.title
                    : config?.title && typeof config.title === 'function' && event
                      ? (config.title(event) ?? event?.type ?? 'Unknown')
                      : (event?.type ?? 'Unknown')}
                </div>

                <div className='text-sm text-left font-lato text-lucent-text'>
                  {((event?: TimelineEvent) => {
                    switch (event?.type) {
                      case undefined: {
                        return <span className='text-lucent-text'>Unknown event</span>
                      }

                      case 'cr-report': {
                        return (
                          <>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=34&file-type-id=${reportFileTypeMapper[event.data.type as keyof typeof reportFileTypeMapper]}&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.name}
                            </a>

                            {event.data.cause === 'manual' && event.data.user && (
                              <>
                                {' '}
                                by{' '}
                                <Tooltip title={event.data.user.email}>
                                  <span className='text-lucent-text'>
                                    {event.data.user.first_name} {event.data.user.last_name}
                                  </span>
                                </Tooltip>
                              </>
                            )}

                            {(!!event.data.meta || !!event.data.totals) && (
                              <Tooltip
                                color='#ffffff'
                                title={
                                  <div className='flex flex-col gap-4 px-3 py-2 text-lucent-text'>
                                    {event.data.meta && (
                                      <>
                                        <p>
                                          This report was compiled with the following{' '}
                                          <strong>group settings</strong>:
                                        </p>

                                        <div className='grid grid-cols-[max-content_1fr] gap-y-1 gap-x-3'>
                                          {formatGroupMeta(event.data.meta.group).map(entry => (
                                            <Fragment key={entry.key}>
                                              <span className='font-lato text-[0.625rem] uppercase font-semibold text-gray-500 text-left'>
                                                {entry.label ?? entry.key}
                                              </span>

                                              <span className='font-lato text-[0.625rem] uppercase font-semibold text-lucent-text text-left'>
                                                {entry.value}
                                              </span>
                                            </Fragment>
                                          ))}
                                        </div>
                                      </>
                                    )}

                                    {event.data.totals && (
                                      <>
                                        <p>
                                          The following <strong>totals</strong> were calculated:
                                        </p>

                                        <div className='grid grid-cols-[max-content_1fr] gap-y-1 gap-x-3'>
                                          {reportsTypes.map(method => {
                                            const reportTotalsMapper: Record<string, string> = {
                                              Check: 'checks',
                                              Epay: 'epay',
                                              ACH: 'ach',
                                              'Network Summary': 'network_summary',
                                              'Network Detail': 'network_detail'
                                            }

                                            const total =
                                              event.data?.totals?.[
                                                reportTotalsMapper[method.value] ?? ''
                                              ]
                                            const amount = total?.payments.amount ?? 0
                                            const voided = -1 * (total?.voids.amount ?? 0)

                                            if (amount === 0 && voided === 0) {
                                              return null
                                            }

                                            return (
                                              <Fragment key={method.value}>
                                                <span className='font-lato text-[0.625rem] uppercase font-semibold text-gray-500 text-left'>
                                                  {method.label}
                                                </span>

                                                <span className='font-lato text-[0.625rem] uppercase font-semibold text-lucent-text text-left'>
                                                  {currencyFormatter.format(amount)}
                                                  {voided > 0 && (
                                                    <>
                                                      {' '}
                                                      <span className='text-lucent-red'>
                                                        (less {currencyFormatter.format(voided)}{' '}
                                                        voided)
                                                      </span>
                                                    </>
                                                  )}
                                                </span>
                                              </Fragment>
                                            )
                                          })}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                }
                              >
                                <SettingOutlined className='text-gray-400 text-sm ml-1.5 -translate-y-[2px]' />
                              </Tooltip>
                            )}
                          </>
                        )
                      }

                      case 'lcr-file': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.original_filename)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.original_filename}
                            </a>{' '}
                            Received from LuminX
                            {typeof event.data.source === 'string' && (
                              <>
                                {' '}
                                via{' '}
                                <span className='underline text-lucent-text underline-offset-8 decoration-gray-300'>
                                  {pickupSourceNameMapper[event.data.source] ?? event.data.source}
                                </span>
                              </>
                            )}
                          </span>
                        )
                      }

                      case 'scheduled-file': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.name}
                            </a>{' '}
                            Sent to EBIX
                          </span>
                        )
                      }

                      case 'nomi835': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/from-path/${encodeURIComponent(event.data.path)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.name}
                            </a>{' '}
                            Received from
                            <span className='p-1 -m-1 mx-0 font-mono text-xs bg-gray-100 rounded text-lucent-text'>
                              SFTP
                            </span>
                          </span>
                        )
                      }

                      case 'nomi_status_file': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/from-path/${encodeURIComponent(event.data.path)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.source} ({event.data.name}:{' '}
                              <b>
                                {event.data.status}
                                {event.data.exceptionType ? `, ${event.data.exceptionType}` : ''}
                              </b>
                              )
                            </a>{' '}
                            Received from
                            <span className='p-1 -m-1 mx-0 font-mono text-xs bg-gray-100 rounded text-lucent-text'>
                              SFTP
                            </span>
                          </span>
                        )
                      }

                      case 'sent-to-zelis': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=2&file-type-id=2&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.filename}
                            </a>{' '}
                            Sent to Zelis
                          </span>
                        )
                      }

                      case 'received-from-zelis': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=3&file-type-id=3&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.filename}
                            </a>{' '}
                            Received from Zelis
                          </span>
                        )
                      }

                      case 'payment-file': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.filename)}?event-type-id=1&file-type-id=1&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.filename}
                            </a>{' '}
                            Received from LuminX
                            {typeof event.data.source === 'string' && (
                              <>
                                {' '}
                                via{' '}
                                <span className='underline text-lucent-text underline-offset-8 decoration-gray-300'>
                                  {pickupSourceNameMapper[event.data.source] ?? event.data.source}
                                </span>
                              </>
                            )}
                          </span>
                        )
                      }

                      case 'notification': {
                        return (
                          <p className='max-w-full'>
                            <span className='p-1 -m-1 mx-0 font-mono text-xs bg-gray-100 rounded text-lucent-text'>
                              {event.data.subject}
                            </span>{' '}
                            sent to{' '}
                            <Tooltip title={event.data.name || undefined}>
                              <span className='p-1 -m-1 mx-0 font-mono text-xs bg-gray-100 rounded text-lucent-text'>
                                {event.data.address}
                              </span>
                            </Tooltip>{' '}
                            via{' '}
                            <span className='p-1 -m-1 mx-0 font-mono text-xs bg-gray-100 rounded text-lucent-text'>
                              {event.data.channel}
                            </span>
                          </p>
                        )
                      }

                      case 'rerun': {
                        return (
                          <span>
                            Rerun by{' '}
                            <Tooltip title={event.data.user.email}>
                              <span className='text-lucent-text'>
                                {event.data.user.first_name} {event.data.user.last_name}
                              </span>
                            </Tooltip>
                          </span>
                        )
                      }

                      case 'void': {
                        return (
                          <span>
                            Voided by{' '}
                            <Tooltip title={event.data.user.email}>
                              <span className='text-lucent-text'>
                                {event.data.user.first_name} {event.data.user.last_name}
                              </span>
                            </Tooltip>
                          </span>
                        )
                      }

                      case 'restore': {
                        return (
                          <span>
                            Restored by{' '}
                            <Tooltip title={event.data.user.email}>
                              <span className='text-lucent-text'>
                                {event.data.user.first_name} {event.data.user.last_name}
                              </span>
                            </Tooltip>
                          </span>
                        )
                      }

                      case 'payment-file-manual': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=1&file-type-id=1&token=${encodeURIComponent(token ?? '')}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.name}
                            </a>{' '}
                            Uploaded by{' '}
                            <Tooltip title={event.data.user.email}>
                              <span className='text-lucent-text'>
                                {event.data.user.first_name} {event.data.user.last_name}
                              </span>
                            </Tooltip>
                          </span>
                        )
                      }

                      case 'lcr-file-manual': {
                        return (
                          <span>
                            <a
                              href={`${API_URL}/files/${encodeURIComponent(event.data.name)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(event.data.id)}`}
                              className='text-gray-700 underline underline-offset-8 decoration-gray-300 hover:underline'
                            >
                              {event.data.name}
                            </a>{' '}
                            Uploaded by{' '}
                            <Tooltip title={event.data.user.email}>
                              <span className='text-lucent-text'>
                                {event.data.user.first_name} {event.data.user.last_name}
                              </span>
                            </Tooltip>
                          </span>
                        )
                      }

                      case 'dropoff': {
                        return <DropoffTimeline event={event} />
                      }

                      default: {
                        return null
                      }
                    }
                  })(event)}
                </div>
              </div>
            )
          }}
          rangeChanged={({ startIndex }) => {
            setYear(year => events[startIndex]?.date.getFullYear() ?? year)
          }}
        />
      </div>
    </div>
  )
}
